/**
 * Wire shapes of the agents and rules topic, and the editor's own types beside them. A
 * manifest's `settings`, `settingGroups` and `outputContract` are the agent author's content,
 * stored as the agent wrote it: each item parses on its own, and its vocabularies are open.
 */

import type { ComponentType } from 'react';
import { z } from 'zod';

import { keyBy, listOf } from '@/api/listOf';

import type { NonEmpty } from './common';
import { DeviceIdSchema } from './deviceId';
import {
    Agent as GeneratedAgentSchema,
    GetRuleSchemaResponse as GeneratedRulesSchemaSchema,
    GetRuleTriggerCountResponse as TriggerCountSchema,
    Rule as GeneratedRuleSchema,
    UpsertRulesResponse as GeneratedRuleCreatedSchema,
} from './generated/vms_database.zod';
import type { LoadState, SettingsSnapshot } from './snapshot';

/**
 * How a manifest setting is edited. A frozen object rather than an enum because the value
 * crosses a JSON boundary. An agent may declare a type this UI does not know; it then gets
 * the text control.
 */
export const AgentSettingType = {
    Boolean: 'boolean',
    Enum: 'enum',
    Number: 'number',
    Integer: 'integer',
    String: 'string',
    Text: 'text',
    HeatMap: 'heatMap',
    Line: 'line',
    Polygon: 'polygon',
} as const;
export type AgentSettingType = (typeof AgentSettingType)[keyof typeof AgentSettingType];
type AgentSettingTypeName = AgentSettingType | (string & NonNullable<unknown>);

const AgentSettingTypeSchema = z.custom<AgentSettingTypeName>(
    (value: unknown): boolean => typeof value === 'string',
);

/** Where a manifest setting applies. Agent authors write it; any other value is agent-level. */
export const AgentSettingScope = {
    DeviceAgent: 'deviceAgent',
} as const;
export type AgentSettingScope = (typeof AgentSettingScope)[keyof typeof AgentSettingScope];
type AgentSettingScopeName = AgentSettingScope | (string & NonNullable<unknown>);

const AgentSettingScopeSchema = z.custom<AgentSettingScopeName>(
    (value: unknown): boolean => typeof value === 'string',
);

/**
 * The id prefix of a setting that a dedicated editor owns, which generic settings forms hide.
 * Keep in step with occurra::ai::kSystemSettingPrefix.
 */
export const SYSTEM_SETTING_PREFIX = 'system.';

/** How an agent takes its input; a newer agent may name a mode this UI does not know. */
type AgentInputMode =
    'live' | 'eventBased' | 'deviceBound' | 'none' | 'metadata' | (string & NonNullable<unknown>);

const AgentInputModeSchema = z.custom<AgentInputMode>(
    (value: unknown): boolean => typeof value === 'string',
);

/** One choice of an `enum` setting. */
export const AgentSettingValueSchema = z.object({
    id: z.string(),
    name: z.string().optional(),
});
export type AgentSettingValue = z.infer<typeof AgentSettingValueSchema>;

/**
 * One editable setting from an agent's manifest. Values travel the wire as strings — the
 * media_server persists them as a string→string map — so the bounds arrive as whatever JSON
 * carried, and the controls convert.
 */
export const AgentSettingSchema = z.object({
    id: z.string(),
    type: AgentSettingTypeSchema.optional(),
    scope: AgentSettingScopeSchema.optional(),
    name: z.string().optional(),
    description: z.string().optional(),
    /** Members of the same group render as one block. */
    group: z.string().optional(),
    default: z.unknown().optional(),
    values: z.array(AgentSettingValueSchema).optional(),
    minimum: z.union([z.number(), z.string()]).optional(),
    maximum: z.union([z.number(), z.string()]).optional(),
    step: z.number().optional(),
    /** `line` / `polygon`: how many geometries the setting accepts. */
    maxCount: z.number().optional(),
    /** `heatMap`: the grid's dimensions in cells. */
    width: z.number().optional(),
    height: z.number().optional(),
});
export type AgentSetting = z.infer<typeof AgentSettingSchema>;

/** A settings group's display name, keyed by the id members carry. */
export const AgentSettingGroupSchema = z.object({
    id: z.string(),
    name: z.string().optional(),
});
export type AgentSettingGroup = z.infer<typeof AgentSettingGroupSchema>;

/** One entry of an agent's `outputContract` — what it emits, under which category. */
export const AgentOutputSchema = z.object({
    category: z.string(),
    predefinedAttributes: z.array(z.string()),
    /** Agent-defined attributes, keyed by name. */
    customAttributes: z.record(z.string(), z.unknown()),
});
export type AgentOutput = z.infer<typeof AgentOutputSchema>;

/** What one analysed segment may cost, as the manifest declares it. */
export const SegmentConfigSchema = z.object({
    maxEventDurationSec: z.number(),
    /** The frames-per-segment cap: the inference engine is sized for it. */
    maxFramesPerBatch: z.number(),
    minFramesPerBatch: z.number(),
    defaultFramesPerBatch: z.number(),
    /** Whether each mode carries its own frame budget, or one covers the agent. */
    perModeFrames: z.boolean(),
});
export type SegmentConfig = z.infer<typeof SegmentConfigSchema>;

/** A mode the manifest ships; a stored mode goes through parseModes. */
export const DefaultModeSchema = z.object({
    parent: z.string(),
    fps: z.number(),
    frames: z.number(),
    /** A manifest synced before the field existed lacks it; absence reads as true. */
    ignoreStillObjects: z.boolean().optional(),
});

/** An agent's manifest, as the analytics server publishes it. */
export const AgentManifestSchema = z.object({
    /** An agent without an id is addressable by no endpoint. */
    id: z.string().min(1),
    name: z.string(),
    version: z.string(),
    description: z.string(),
    inputMode: AgentInputModeSchema,
    videoFormat: z.string(),
    executionTarget: z.string(),
    /** Agents of one group share a settings tab. */
    agentGroup: z.string(),
    settings: listOf(AgentSettingSchema, 'settings', { getKey: keyBy('id') }),
    settingGroups: listOf(AgentSettingGroupSchema, 'settingGroups', { getKey: keyBy('id') }),
    /** Pipe-joined `SimilarityMode` flags, e.g. `phraseSimilarity|sceneDeviation`. */
    similarityModes: z.string(),
    /** What the agent emits; the rule editor reads categories and attributes. */
    outputContract: listOf(AgentOutputSchema, 'outputContract', { getKey: keyBy('category') }),
    /** Limits on one analysed segment; the action settings clamp against them. */
    segmentConfig: SegmentConfigSchema.optional(),
    /** The default mode's rate; 0 means every frame. */
    defaultFps: z.number(),
    /** The rate range a mode may be set to; 0 on either side is unbounded. */
    minFps: z.number(),
    maxFps: z.number(),
    /** The pictogram id the modes editor shows for this agent; '' shows none. */
    icon: z.string(),
    /** The modes an agent ships with, used when the device stores none. */
    defaultModes: z.array(DefaultModeSchema),
    /** The conditions a special mode may key off: agent groups and agent ids. */
    availableParents: z.array(z.string()),
    /** Whether a rule condition may name the agent to review its event. Absent on an older
     *  server, which has no such agent. */
    canUseForReview: z.boolean().optional(),
});
export type AgentManifest = z.infer<typeof AgentManifestSchema>;

/**
 * One row of the agents list: the manifest, and the server that runs it. A row without a server
 * is on every server.
 */
export const AgentSchema = GeneratedAgentSchema.extend({ manifest: AgentManifestSchema });
export type Agent = z.infer<typeof AgentSchema>;

/** The agents list as its api atom holds it. */
export interface AgentsSnapshot {
    agents: Agent[];
    /** A load has succeeded: a reload holds rows, a first load does not. */
    isLoaded: boolean;
    isLoading: boolean;
    error: Error | null;
}

const AgentKeySchema = z.object({ manifest: z.object({ id: z.string() }) });

/** A row's key in a dropped-row report; an empty id names nothing, so the row's index does. */
export function getAgentKey(row: unknown): string | null {
    const keyed = AgentKeySchema.safeParse(row);
    return keyed.success && keyed.data.manifest.id !== '' ? keyed.data.manifest.id : null;
}

/**
 * An agent's stored settings, agent-level or per device: a flat string map, whose JSON-valued
 * entries (`system.modes`, the geometries, the motion mask) their readers parse.
 */
export const AgentSettingsSchema = z.record(z.string(), z.string());
export type AgentSettings = z.infer<typeof AgentSettingsSchema>;

/** One agent's stored settings as its api atom holds them; null until a load succeeds. */
export type AgentSettingsSnapshot = SettingsSnapshot<AgentSettings>;

/** The settings of one agent on one camera. */
export interface AgentSettingsKey {
    agentId: string;
    cameraId: string;
}

/** The settings of one agent on a set of cameras. */
export interface AgentSettingsSetKey {
    agentId: string;
    cameraIds: readonly string[];
}

/** The settings of one agent on a set of cameras, as its readers merge them. */
export interface AgentSettingsSet {
    /** Each camera whose settings loaded; a camera whose load failed is absent. */
    byCamera: Readonly<Record<string, AgentSettings>>;
    /** True while a camera has no settings and no error. */
    isLoading: boolean;
}

/**
 * One activation mode of the `system.modes` per-device setting. The record
 * whose `parent` is '' is the DEFAULT mode, running whenever no special
 * mode's gate is open; every other record runs while its parent's gate is.
 */
export interface ActivationMode {
    /** An agent group or a direct agent id; '' is the default mode. */
    parent: string;
    /** On the default mode 0 disables it; at or above the unlimited sentinel it is every frame. */
    fps: number;
    /** 0 means the manifest's own budget. */
    frames: number;
    /** Special modes only; absent reads as true, as the toolbox reads it. */
    ignoreStillObjects?: boolean;
}

/**
 * A mode record as it is stored or shipped: only the parent is always there,
 * and parseModes fills the rest from the manifest's own defaults.
 */
export type ActivationModeRecord = Partial<ActivationMode> & { parent: string };

/** Exactly one default record, first; then one per parent. */
export type NormalizedModes = NonEmpty<ActivationMode>;

/** A bound the picker narrows itself to. */
export interface NumericRange {
    min: number;
    max: number;
}

/** Which side of a filter an operand reads: an event's parameter, or a literal. */
export const OperandSourceSchema = z.enum(['eventParameter', 'constant']);
export type OperandSource = z.infer<typeof OperandSourceSchema>;

/** One side of a rule filter; loose, as the whole rule is sent back. */
export const OperandSchema = z.looseObject({
    source: OperandSourceSchema,
    /** The parameter name, for an `eventParameter` operand. */
    key: z.string(),
    /** The literal, for a scalar `constant`. */
    value: z.string().optional(),
    /** The members, for a list `constant` — the `inList` / `notInList` right side. */
    values: z.array(z.string()).optional(),
});
export type Operand = z.infer<typeof OperandSchema>;

/** The engine's operators; a newer engine's operator arrives as it is. */
export type FilterOperator =
    'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'notContains' |
    'startsWith' | 'inList' | 'notInList' | 'textSimilarity' | 'sceneDeviation' |
    (string & NonNullable<unknown>);

const FilterOperatorSchema = z.custom<FilterOperator>(
    (value: unknown): boolean => typeof value === 'string',
);

/** One condition an event must meet. `operator_` is spelled as the engine serializes it. */
export const RuleFilterSchema = z.looseObject({
    left: OperandSchema,
    operator_: FilterOperatorSchema,
    right: OperandSchema,
    /** Operator settings: `threshold`, `subtractBackground`, `listType`. */
    params: z.record(z.string(), z.string()).optional(),
});
export type RuleFilter = z.infer<typeof RuleFilterSchema>;

/**
 * One event block of a rule. Blocks carry no id — they are identified by their index within
 * the rule, which is what the engine's chain state keys on.
 */
export const RuleEventSchema = z.looseObject({
    /** `analyticsEvent` is the only kind the engine emits today; the schema names them. */
    category: z.string(),
    /** Empty is the wildcard: any source. */
    sourceIds: z.array(z.string()),
    /** The engine's conditions. vms-database writes them from `preset`, and the editor reads
     *  them only on an event without one. */
    filters: z.array(RuleFilterSchema),
    /** The event preset the event is, by id; absent on an event of raw filters. */
    preset: z.string().optional(),
    /** The preset's parameters, by name. */
    params: z.record(z.string(), z.unknown()).optional(),
    /** Absent means true, both for a draft and for a rule stored before the field existed. */
    ignoreStillObjects: z.boolean().optional(),
});
export type RuleEvent = z.infer<typeof RuleEventSchema>;

/** One action block of a rule, identified by its index like an event block. */
export const RuleActionSchema = z.looseObject({
    /** `runAnalytics`, `notify` or `playSound` today; the schema names them. */
    category: z.string(),
    /** Empty or absent is a targetless action. */
    targetIds: z.array(z.string()).optional(),
    /** Gates opening the action on a chain; absent is 0, no cooldown. */
    cooldownS: z.number().optional(),
    /** Free-form per-action settings, keyed by the schema's setting keys. */
    settings: z.record(z.string(), z.string()),
});
export type RuleAction = z.infer<typeof RuleActionSchema>;

/**
 * One condition of the IF step, identified by its index like an event block. When an event
 * fires, the rule runs its actions only if each of its conditions holds.
 */
export const RuleConditionSchema = z.looseObject({
    /** The id of the reviewing agent: one whose manifest has canUseForReview. */
    category: z.string(),
    /** Empty is the camera of the event that fired. */
    sourceIds: z.array(z.string()),
    /** Free-form per-condition settings, keyed by SCENE_INSIGHT_SETTING. */
    settings: z.record(z.string(), z.string()),
});
export type RuleCondition = z.infer<typeof RuleConditionSchema>;

export const EventLogicSchema = z.enum(['logicOr', 'logicAnd']);

/**
 * How a rule limit ends the rule. A frozen object rather than an enum, because the kind arrives as
 * the `kind` of a stored limit.
 */
export const RuleLimitKind = {
    /** After a number of triggers. */
    Triggers: 'triggers',
    /** At a time. */
    Until: 'until',
} as const;
export type RuleLimitKind = (typeof RuleLimitKind)[keyof typeof RuleLimitKind];

/** When a rule disables itself, as the limit dialog and banner see it. */
export interface RuleLimit {
    kind: RuleLimitKind;
    /** For `triggers`: how many times the rule triggers before it disables itself. */
    count?: number;
    /** For `until`: the ISO 8601 time at which the rule disables itself. */
    until?: string;
}

/** Loose at every level: the editor sends a rule back whole. */
export const RuleSchema = GeneratedRuleSchema.extend({
    events: z.array(RuleEventSchema),
    actions: z.array(RuleActionSchema),
    /** Absent or empty: the rule has no IF step. */
    conditions: z.array(RuleConditionSchema).optional(),
}).loose();
export type Rule = z.infer<typeof RuleSchema>;

/** The organization's rules, as the store holds them. */
export interface RulesSnapshot {
    rules: Rule[];
    isLoading: boolean;
    error: Error | null;
}

/** One rule an editor has open; null until it loads. */
export interface RuleSnapshot {
    rule: Rule | null;
    isLoading: boolean;
    error: Error | null;
}

/** How often a rule has triggered toward its limit; null until it loads. */
export interface TriggerCountSnapshot extends LoadState {
    ruleId: string;
    count: number | null;
}

/** What the limit dialog asks for. A period becomes an `until` limit when the dialog saves. */
export enum LimitChoice {
    Triggers = 'triggers',
    Period = 'period',
    Time = 'time',
}

export enum PeriodUnit {
    Minutes = 'minutes',
    Hours = 'hours',
    Days = 'days',
}

/** The fields of the limit dialog. */
export interface LimitForm {
    choice: LimitChoice;
    count: number;
    period: number;
    unit: PeriodUnit;
    /** A wall-clock time of the operator, as a `datetime-local` input writes it. */
    time: string;
}

/** The answer to GET /rules/{id}/triggers: how often the rule has triggered toward its limit. */
export { TriggerCountSchema };

/** An event as a write sends it: one with a preset goes without filters, which vms-database
 *  writes from the preset. */
export type RuleEventDraft = Omit<RuleEvent, 'filters'> & Partial<Pick<RuleEvent, 'filters'>>;

/** What a rule create sends: the fields it sets, the server keeping the rest. */
export type RuleDraft = Omit<Partial<Rule>, 'events'> & { events?: RuleEventDraft[] };

/** A patch of a rule. Its options are always sent: null clears one, a missing key keeps it. */
export type RulePatch = Omit<RuleDraft, 'schedule' | 'triggerLimit' | 'expiresAt'> & {
    schedule: string | null;
    triggerLimit: number | null;
    expiresAt: string | null;
};

/** The cloud's answer to a batch upsert of one rule. */
export const RuleCreatedSchema = GeneratedRuleCreatedSchema.extend({ accepted: z.literal(1) });

/** One `{value, label}` choice of an `enum` value source. */
export const RuleEnumOptionSchema = z.object({
    value: z.string(),
    label: z.string(),
});
export type RuleEnumOption = z.infer<typeof RuleEnumOptionSchema>;

/**
 * How the editor renders one schema setting or parameter — the schema's `valueSource`,
 * discriminated by `type`. Everything but the type is the control's own copy and bounds, so
 * the editor stays schema-driven.
 */
export const RuleValueSourceSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('device'),
        filter: z.string().optional(),
        multiple: z.boolean().optional(),
    }),
    z.object({
        type: z.literal('agent'),
        filter: z.string().optional(),
    }),
    z.object({ type: z.literal('user') }),
    z.object({ type: z.literal('boolean') }),
    /** A blob from the binary-file store, narrowed to a MIME prefix (e.g. "audio/"). */
    z.object({
        type: z.literal('binaryFile'),
        contentTypePrefix: z.string().optional(),
    }),
    z.object({
        type: z.literal('enum'),
        label: z.string().optional(),
        anyLabel: z.string().optional(),
        options: z.array(RuleEnumOptionSchema).optional(),
        /** The bare values, when each is its own label. */
        values: z.array(z.string()).optional(),
    }),
    z.object({
        type: z.literal('seconds'),
        label: z.string().optional(),
        min: z.number().optional(),
        max: z.number().optional(),
        defaultValue: z.string().optional(),
    }),
    z.object({
        type: z.literal('number'),
        min: z.number().optional(),
        max: z.number().optional(),
        default: z.number().optional(),
    }),
    z.object({
        type: z.literal('optionalDuration'),
        noneLabel: z.string().optional(),
        fixedLabel: z.string().optional(),
        defaultFixedValue: z.string().optional(),
    }),
    z.object({
        type: z.literal('optionalFps'),
        noneLabel: z.string().optional(),
        fixedLabel: z.string().optional(),
    }),
    z.object({
        type: z.literal('agentOutputCategories'),
        dependsOn: z.string().optional(),
    }),
    z.object({
        type: z.literal('agentDeviceSettingList'),
        /** The per-device agent setting the values are read from, e.g. `lines`. */
        settingId: z.string().optional(),
        itemLabelKey: z.string().optional(),
        dependsOn: z.string().optional(),
        label: z.string().optional(),
        anyLabel: z.string().optional(),
        hint: z.string().optional(),
    }),
    z.object({
        type: z.literal('enrollmentList'),
        listType: z.string().optional(),
        label: z.string().optional(),
        anyLabel: z.string().optional(),
        notLabel: z.string().optional(),
        hint: z.string().optional(),
    }),
    z.object({
        type: z.literal('phrase'),
        label: z.string().optional(),
        placeholder: z.string().optional(),
        hint: z.string().optional(),
        defaultThreshold: z.string().optional(),
        minThreshold: z.number().optional(),
        maxThreshold: z.number().optional(),
    }),
]);
export type RuleValueSource = z.infer<typeof RuleValueSourceSchema>;

const SettingEqualsSchema = z.object({
    key: z.string(),
    value: z.string(),
});

/** What an agent must emit for a setting or filter to be offered at all. */
export const RuleCapabilityGateSchema = z.object({
    agentEmitsAttribute: z.string().optional(),
    agentHasTextEmbedding: z.boolean().optional(),
    /** The agent emitting the category claims this similarity mode, e.g. `phraseDelta`. */
    agentHasSimilarityMode: z.string().optional(),
    /** Offer this setting only while a sibling setting holds the given value. */
    settingEquals: SettingEqualsSchema.optional(),
    /** Offer this setting unless a sibling holds the value; unset counts as not equal. */
    settingNotEquals: SettingEqualsSchema.optional(),
});
export type RuleCapabilityGate = z.infer<typeof RuleCapabilityGateSchema>;

/** Whether an event or action is a moment or a state. */
export const RuleLifecycleModeSchema = z.enum(['instant', 'prolonged']);
export type RuleLifecycleMode = z.infer<typeof RuleLifecycleModeSchema>;

/**
 * One event parameter the schema declares. `filterable` ones become the editor's condition
 * strip, each rendered from its `valueSource`.
 */
export const RuleParameterSchemaSchema = z.object({
    key: z.string(),
    type: z.string(),
    description: z.string(),
    filterable: z.boolean(),
    operators: z.array(FilterOperatorSchema).optional(),
    required: z.boolean().optional(),
    valueSource: RuleValueSourceSchema.optional(),
    filterableWhen: RuleCapabilityGateSchema.optional(),
});
export type RuleParameterSchema = z.infer<typeof RuleParameterSchemaSchema>;

/** One action setting the schema declares, rendered from its `valueSource`. */
export const RuleSettingSchemaSchema = z.object({
    key: z.string(),
    type: z.string(),
    description: z.string(),
    optional: z.boolean().optional(),
    valueSource: RuleValueSourceSchema.optional(),
    default: z.string().optional(),
    defaultHint: z.string().optional(),
    availableWhen: RuleCapabilityGateSchema.optional(),
    /** Offered only while some configured event is prolonged. */
    requiresEventMode: RuleLifecycleModeSchema.optional(),
});
export type RuleSettingSchema = z.infer<typeof RuleSettingSchemaSchema>;

/** Where a card shows a setting. A frozen object, because the placement arrives in the schema. */
export const ControlPlacement = {
    /** With the settings that define the event. */
    Main: 'main',
    /** Under the collapsed Advanced settings: a setting that narrows the event. */
    Advanced: 'advanced',
} as const;
const PlacementSchema = z.enum([ControlPlacement.Main, ControlPlacement.Advanced]);

const ControlLabelSchema = {
    label: z.string(),
    hint: z.string().optional(),
    placement: PlacementSchema.optional(),
};

/** One choice of an `enum` or `choice` control. */
const ControlOptionSchema = z.object({
    value: z.union([z.string(), z.boolean()]),
    label: z.string(),
    hint: z.string().optional(),
});

/** How the rule editor draws a preset parameter; a parameter without one is not shown. */
export const RulePresetControlSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('text'), ...ControlLabelSchema, placeholder: z.string().optional(),
    }),
    z.object({
        type: z.literal('slider'),
        ...ControlLabelSchema,
        step: z.number().optional(),
        /** The parameter that must be set for the slider to take a value. */
        enabledWhen: z.string().optional(),
        disabledHint: z.string().optional(),
    }),
    z.object({
        type: z.literal('choice'), ...ControlLabelSchema, options: z.array(ControlOptionSchema),
    }),
    z.object({ type: z.literal('checkbox'), ...ControlLabelSchema }),
    z.object({
        type: z.literal('objectTypes'),
        ...ControlLabelSchema,
        /** The category the tree of types is under. */
        root: z.string(),
    }),
    z.object({
        type: z.literal('enum'),
        ...ControlLabelSchema,
        anyLabel: z.string().optional(),
        options: z.array(ControlOptionSchema),
    }),
    z.object({
        type: z.literal('agentDeviceSettingList'),
        ...ControlLabelSchema,
        anyLabel: z.string().optional(),
        /** The per-device agent setting the choices are read from, e.g. `lines`. */
        settingId: z.string(),
        itemLabelKey: z.string().optional(),
    }),
    z.object({
        type: z.literal('seconds'),
        ...ControlLabelSchema,
        /** The value the setting takes when it is turned on. */
        defaultValue: z.number().optional(),
    }),
    z.object({
        type: z.literal('enrollmentList'),
        ...ControlLabelSchema,
        notLabel: z.string().optional(),
        /** The parameter that says whether the list excludes its members. */
        negateParam: z.string(),
    }),
    z.object({
        type: z.literal('phrase'),
        ...ControlLabelSchema,
        placeholder: z.string().optional(),
        /** The parameter that holds the phrase's similarity threshold. */
        thresholdParam: z.string(),
        thresholdLabel: z.string().optional(),
    }),
]);
export type RulePresetControl = z.infer<typeof RulePresetControlSchema>;
/** A control of the given type. */
export type RulePresetControlOf<T extends RulePresetControl['type']> =
    Extract<RulePresetControl, { type: T }>;

/** One parameter an event preset declares. */
export const RulePresetParamSchema = z.object({
    /** `string`, `number`, `integer`, `boolean`, `enum`, `list` or `categories`. */
    type: z.string(),
    min: z.number().optional(),
    max: z.number().optional(),
    /** The choices of an enum. */
    values: z.array(z.string()).optional(),
    default: z.unknown().optional(),
    control: RulePresetControlSchema.optional(),
    /** What the agents of the event's cameras must do for the control to be offered. */
    availableWhen: RuleCapabilityGateSchema.optional(),
});
export type RulePresetParam = z.infer<typeof RulePresetParamSchema>;

/** A filter of a preset's template: a rule filter whose values may be `{param}` placeholders. */
export const RulePresetFilterSchema = z.looseObject({
    optional: z.boolean().optional(),
    left: OperandSchema,
    operator_: z.string(),
    right: z.looseObject({
        value: z.string().optional(),
        values: z.union([z.string(), z.array(z.string())]).optional(),
    }),
    params: z.record(z.string(), z.string()).optional(),
});
export type RulePresetFilter = z.infer<typeof RulePresetFilterSchema>;

/** An event preset: one card of the editor, which vms-database expands into the filters. */
export const RulePresetSchema = z.object({
    id: z.string().min(1),
    name: z.string(),
    description: z.string(),
    /** The Add-event menu group, by id. */
    group: z.string().optional(),
    /** The glyph of the preset, by name. */
    icon: z.string().optional(),
    /** What an installed agent must do for the preset to be offered. */
    availableWhen: RuleCapabilityGateSchema.optional(),
    params: z.record(z.string(), RulePresetParamSchema),
    filters: z.array(RulePresetFilterSchema),
});
export type RulePreset = z.infer<typeof RulePresetSchema>;

/** A condition the engine scores on each evaluation and streams to the live tuning monitor. */
export interface ScoredCondition {
    /** The embedding scored: the scene's, or the object appearance's. */
    key: string;
    operator: string;
    query: string;
    /** Absent when the event holds none. */
    threshold?: number;
    shouldSubtractBackground: boolean;
}

export const RuleEventTypeSchema = z.object({
    /** An entry without a category cannot be chosen. */
    category: z.string().min(1),
    name: z.string(),
    description: z.string(),
    sourceType: z.string(),
    /** `mixed` defers to `sourceCategoryModes`, keyed by the category's root. */
    mode: z.union([RuleLifecycleModeSchema, z.literal('mixed')]),
    sourceCategoryModes: z.record(z.string(), RuleLifecycleModeSchema).optional(),
    parameters: listOf(RuleParameterSchemaSchema, 'parameters', { getKey: keyBy('key') }),
    /** The groups of the Add-event menu, in order. */
    presetGroups: z.array(z.object({ id: z.string(), label: z.string() })).optional(),
    presets: listOf(RulePresetSchema, 'presets', { getKey: keyBy('id') }).optional(),
});
export type RuleEventType = z.infer<typeof RuleEventTypeSchema>;

export const RuleActionTypeSchema = z.object({
    /** An entry without a category cannot be chosen. */
    category: z.string().min(1),
    mode: RuleLifecycleModeSchema,
    name: z.string(),
    description: z.string(),
    settings: listOf(RuleSettingSchemaSchema, 'settings', { getKey: keyBy('key') }),
});
export type RuleActionType = z.infer<typeof RuleActionTypeSchema>;

/** Static menu of event/action types the rule editor offers. */
export const RulesSchemaSchema = GeneratedRulesSchemaSchema.extend({
    eventTypes: listOf(RuleEventTypeSchema, 'eventTypes', { getKey: keyBy('category') }),
    actionTypes: listOf(RuleActionTypeSchema, 'actionTypes', { getKey: keyBy('category') }),
});
export type RulesSchema = z.infer<typeof RulesSchemaSchema>;

/** The rules schema as the app holds it: empty until it loads, so the editor renders at once. */
export interface RulesSchemaSnapshot {
    schema: RulesSchema;
    isLoading: boolean;
    error: Error | null;
}

/** One object type of the Object detection card: a checkbox of its tree. */
export interface ObjectTypeNode {
    /** The full category, e.g. "object.vehicle.car". */
    category: string;
    label: string;
    children: ObjectTypeNode[];
}

/** A menu entry's glyph, rendered as a component — a lucide icon in practice. */
export type RuleMenuIcon = ComponentType<{ className?: string }>;

/** One entry of the editor's Add-event menu: an event preset. */
export interface AddEventItem {
    /** The event type the preset is of. */
    category: string;
    preset: RulePreset;
    icon: RuleMenuIcon;
}

/** One labelled group of the Add-event menu. */
export interface AddEventGroup {
    label: string;
    items: AddEventItem[];
}

/** One entry of the editor's Add-action menu. */
export interface AddActionItem {
    category: string;
    label: string;
    description: string;
    icon: RuleMenuIcon;
}

/** One entry of the editor's Add-condition menu. */
export interface AddConditionItem {
    /** The id of the reviewing agent, the category of the new condition. */
    kind: string;
    label: string;
    icon: RuleMenuIcon;
}

/** A step of a rule as the editor shows it: the events, the conditions or the actions. */
export enum RuleStep {
    When = 'when',
    If = 'if',
    Do = 'do',
}

/**
 * Why a monitor frame carries the score it does — `ProbeState` in the engine's
 * filter_evaluator.h. Every state but `waiting` comes from the engine; `waiting` is the
 * monitor's own "stream open" marker. The engine's set grows; a state this build does not know
 * reads as `unknown`, and its frame still charts its score.
 */
export const ProbeStateSchema = z.string().pipe(
    z.enum([
        'waiting', 'scored', 'warmingUp', 'waitingForQuery', 'incompatible', 'notScored',
        'unknown',
    ]).catch('unknown'),
);
export type ProbeState = z.infer<typeof ProbeStateSchema>;

/**
 * One scored evaluation of one rule condition, as the tuning monitor streams
 * it — the engine's `ProbeSample`, which is the SSE frame itself. Blocks carry
 * no ids, so a frame names its condition by (eventIndex, filterIndex).
 */
export const ProbeSampleSchema = z.object({
    ruleId: z.string(),
    eventIndex: z.number().int(),
    filterIndex: z.number().int(),
    cameraId: DeviceIdSchema,
    utcTimeMs: z.number().int(),
    /** In [0, 1], on a `scored` frame only. */
    score: z.number().optional(),
    state: ProbeStateSchema,
    /** Warm-up progress, for the `warmingUp` state. */
    warmupElapsedMs: z.number().int(),
    warmupRequiredMs: z.number().int(),
});
export type ProbeSample = z.infer<typeof ProbeSampleSchema>;

/** One charted score: when it was evaluated, and what it scored. */
export interface ChartPoint {
    t: number;
    score: number;
}

/** What the monitor knows about one scored condition, folded from its frames. */
export interface RuleChart {
    points: ChartPoint[];
    state: ProbeState;
    warmupElapsedMs: number;
    warmupRequiredMs: number;
    lastUtcTimeMs: number;
}

/** Every chart of a rule, keyed `eventIndex:filterIndex`. */
export type RuleCharts = ReadonlyMap<string, RuleChart>;

/** Where the monitor's stream stands; the charts are stale in `reconnecting`. */
export type MonitorStatus = 'idle' | 'connecting' | 'live' | 'reconnecting';

/** A point of the level chart, in pixels: x grows to the right, y grows down. */
export interface PlotPoint {
    x: number;
    y: number;
}

export interface RuleSet {
    version: number;
    id: string;
    name: string;
    isEnabled: boolean;
}

/** Normalized [0,1] coordinate pair, as the geometry settings store them. */
export type Point = [number, number];

export type LineDirection = 'AB' | 'BA' | 'both';

/** A `line`-typed agent setting: a crossing line with a counted direction. */
export interface Line {
    id: string;
    name: string;
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    /** Side A is to the LEFT of the P1->P2 vector on screen. */
    direction: LineDirection;
}

export type AreaTrigger = 'enter' | 'leave';

/** A `polygon`-typed agent setting: a zone, with an optional dwell trigger. */
export interface Area {
    id: string;
    name: string;
    points: Point[];
    /** Dwell before the zone fires, in seconds; 0 disables it. */
    loiterS: number;
    trigger: AreaTrigger;
}

/**
 * Not a wire shape: what a draft store publishes — the geometry being edited
 * plus the mutators the video overlay drives it with. `isActive` is false while
 * no setting is open for the camera.
 */
export interface GeometryDraft<T> {
    isActive: boolean;
    items: T[];
    selectedId: string | null;
    maxCount: number;
    select(id: string | null): void;
    /** Replaces one item (geometry or fields) and pushes it through the form. */
    update(id: string, patch: Partial<T>): void;
}

export type LineDraft = GeometryDraft<Line>;

export type AreaDraft = GeometryDraft<Area>;
